
import { memo, useState } from 'react'
import toast from 'react-hot-toast'

import copy from 'copy-to-clipboard'

import { ideaBulb } from '@assets'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ApiCaller } from '@frontend/api'
import { Cursor, Markdown } from '@ui/components'
import markdown from '@wcj/markdown-to-html'

import { useChatContext } from '../../ChatProvider'
import { useMessageContext } from '../../modules/Message'
import MessageSources from '../sources/MessageSources'
import TipsModal from '../TipsModal'
import BrainsUsed from './BrainsUsed'
import MessageActions from './MessageActions'
import ReasoningDropDown from './ReasoningDropDown'
import Retry from './Retry'
import SuggestedPrompts from './SuggestedPrompts'

// eslint-disable-next-line complexity
function MessageContent() {
	const { message, isWritting, children, onSourceSelected } = useMessageContext()
	const options = useChatContext()
	const [isTipsOpen, setIsTipsOpen] = useState(false)

	const { chatState } = options

	const displayActions = options.displayActions && !isWritting

	async function handleCopy() {
		ApiCaller.track('Copied_message_from_chat')
		const cleanedContent = message.content
			?.replace(/<source.*?>/g, '')
			?.replace(/<\/source>/g, '') ?? ''

		copy(markdown(cleanedContent) as string, {
			format: 'text/html'
		})
		toast.success('Text copied to clipboard')
	}

	return (
		<div className="min-h-[20px] w-full flex flex-col items-start gap-2">
			<div className="chat-message break-words w-full">
				{ message.role === 'user' && <BrainsUsed /> }
				{ message.thinkingContent && message.role === 'assistant'
					? <ReasoningDropDown
						reasoningContent={ message.thinkingContent }
						isThinking={ !message.content && chatState.isThinkingOrAnswering }
						isDeepResearch={ message.openBrain === 'perplexity' }
						onSourceSelected={ onSourceSelected }
					/>
					: null }
				{
					children
						? children
						: (
							<>
								{ options.type === 'chatbot' && options.botIcon && message.role === 'assistant'
									? (
										<div className="flex gap-3">
											<img
												src={ options.botIcon }
												alt=""
												className="h-6" />
											<div>
												<Markdown
													contents={ message.contents }
													isWriting={ isWritting }
													onSourceSelected={ onSourceSelected }
												>
													{ message.content ?? '' }
												</Markdown>
											</div>
										</div>
									)
									: <Markdown
										contents={ message.contents }
										isWriting={ isWritting }
										onSourceSelected={ onSourceSelected }
									>
										{ message.content ?? '' }
									</Markdown> }

								{ message.role === 'assistant' && options.type === 'chat' && message.status === 'processed' && message.content
									? <div className="flex justify-end" style={ { margin: '0px' } }>
										<button
											className="text-gray-400 hover:text-gray-600 transition-colors"
											onClick={ () => {
												if (message.content) {
													handleCopy()
												}
											} }
										>
											<FontAwesomeIcon icon="clipboard" className="w-4 h-4" />
										</button>
									</div>
									: null }
								{
									isWritting
										? <span className="w-3 h-3 inline-block blink mb-1">
											<Cursor />
										</span>
										: null
								}
							</>
						)
				}
			</div>
			{ displayActions ? <MessageActions /> : null }

			{
				message.status === 'processed' && (
					options.displaySources
						? (
							<MessageSources />
						)
						: (
							options.displayTips && (
								<div>
									<div
										className="flex items-center bg-white border border-gray-200 text-gray-400 rounded-full px-3 py-1 m-1 text-sm w-fit"
									>
										<img
											className="h-5 w-5 mr-2"
											src={ ideaBulb }
											alt="icon"
										/>

										<button
											type="button"
											onClick={ () => {
												setIsTipsOpen(true)
											} }
										>
										Tips to use VirtualBrain
										</button>
									</div>
								</div>
							)
						)
				)
			}

			<SuggestedPrompts />

			{ message.status === 'failed' && <Retry /> }

			<TipsModal
				isOpen={ isTipsOpen }
				onClose={ () => {
					setIsTipsOpen(false)
				} }
			/>
		</div>
	)
}

export default memo(MessageContent)
